import React from "react";
import {
  Card,
  CardProps,
  CardHeader,
  Typography,
  Paper,
  Box,
  CardActions,
  Button,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { graphql } from "react-relay";
import { useMutation } from "react-relay/hooks";
import { useFragment } from "react-relay/hooks";
import { MoreHoriz } from "@mui/icons-material";
import { ConnectionHandler } from "relay-runtime";
import {
  usePopupState,
  bindTrigger,
  bindMenu,
} from "material-ui-popup-state/hooks";

import { useSnackAlert } from "../../hooks/useSnackAlert";
import { CardAvatar } from "../card/CardAvatar";
import { ListMenu } from "../menu/ListMenu";
import { EnrollmentScore } from "../progress/EnrollmentScore";
import { colorSystem } from "../../theme";

import { EnrollmentCard_enrollment$key } from "./__generated__/EnrollmentCard_enrollment.graphql";
import { useNavigate } from "react-router-dom";
import {
  COACH_CLIENT_OVERVIEW_ROUTE,
  COACH_CLIENT_PR0GRAMS_ROUTE,
} from "../../routes/routes";
import { PaginationContext } from "../../utils/paging";

const useStyles = makeStyles((theme) => ({
  actionArea: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: theme.palette.text.primary,
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
    },
  },

  header: {
    maxWidth: 440,
    width: "50%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%",
    },
  },

  cardTitle: {
    fontSize: 24,
    fontWeight: 600,
    color: theme.palette.common.black,
  },

  cardSubheader: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },

  text: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },

  avatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    borderRadius: theme.spacing(1),

    [theme.breakpoints.down("sm")]: {
      width: theme.spacing(5),
      height: theme.spacing(5),
    },
  },

  metrics: {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    justifyContent: "space-between",
    padding: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      flexDirection: "row-reverse",
      justifyContent: "flex-end",
    },
  },

  actions: {
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      width: "100%",
    },
  },

  menuButton: {
    minWidth: theme.spacing(5.5),
    width: theme.spacing(5.5),
    height: theme.spacing(5.5),
    backgroundColor: colorSystem.secondaryGray,
  },
}));

const fragment = graphql`
  fragment EnrollmentCard_enrollment on Enrollment {
    id
    client {
      id
      username
      displayName
      email
      photoURL
    }
    startDate(utc: true, format: "MMM Do")
    endDate(utc: true, format: "MMM Do")
    completion(period: BEFORE_NOW) {
      unlockedScore: rate
    }
    viaGroup
  }
`;

const unenrollClientMutation = graphql`
  mutation EnrollmentCardUnenrollMutation($input: UnenrollClientInput!) {
    unenrollClient(input: $input) {
      clientMutationId
      client {
        ...ClientAddableItem_client
      }
    }
  }
`;

export interface EnrollmentCardProps extends CardProps {
  enrollmentRef: EnrollmentCard_enrollment$key;
}

export function EnrollmentCard(props: EnrollmentCardProps) {
  const { enrollmentRef, ...other } = props;
  const navigate = useNavigate();
  const s = useStyles();
  const snackAlert = useSnackAlert();
  const { programSlug, filter } = React.useContext(PaginationContext);

  const enrollment = useFragment(fragment, enrollmentRef);
  const {
    id,
    client,
    startDate,
    endDate,
    completion: { unlockedScore },
    viaGroup,
  } = enrollment;

  const menuState = usePopupState({
    variant: "popover",
    popupId: "clientMenu",
  });
  const triggerProps = React.useMemo(() => bindTrigger(menuState), [menuState]);
  const menuProps = React.useMemo(() => bindMenu(menuState), [menuState]);

  const [unenrollClient, unenrollClientInFlight] = useMutation(
    unenrollClientMutation,
  );

  const unenrollClientStoreUpdater = React.useCallback(
    (store) => {
      const enrollmentsAvatars = ConnectionHandler.getConnection(
        store.getRoot(),
        "Program_enrollmentsAvatars",
        [],
      );
      const enrollments = ConnectionHandler.getConnection(
        store.getRoot(),
        "Program_enrollments",
        {
          first: null,
          after: null,
          orderBy: filter,
          programSlug: programSlug,
        },
      );

      if (enrollmentsAvatars && enrollments) {
        ConnectionHandler.deleteNode(enrollmentsAvatars, id);
        ConnectionHandler.deleteNode(enrollments, id);

        const totalCount = enrollments.getValue("totalCount") as number;
        const newTotalCount = Math.max(totalCount - 1, 0);

        enrollmentsAvatars.setValue(newTotalCount, "totalCount");
        enrollments.setValue(newTotalCount, "totalCount");
      }
    },
    [id, filter],
  );

  const handleViewClient = React.useCallback(() => {
    navigate(COACH_CLIENT_OVERVIEW_ROUTE.replace(":username", client.username));
  }, [client]);

  const handleViewClientProgress = React.useCallback(() => {
    navigate(COACH_CLIENT_PR0GRAMS_ROUTE.replace(":username", client.username));
  }, [client]);

  const handleUnenrollClient = React.useCallback(() => {
    menuProps.onClose();

    const input = { id };
    unenrollClient({
      variables: { input },
      onCompleted: () => {
        snackAlert({
          severity: "success",
          message: "Enrollment has been removed.",
        });
      },
      updater: unenrollClientStoreUpdater,
    });
  }, [id, menuProps, snackAlert, unenrollClient, unenrollClientStoreUpdater]);

  const menuOptions = [
    {
      label: "View Progress",
      onClick: handleViewClientProgress,
    },

    {
      label: "Remove from Program",
      onClick: handleUnenrollClient,
      disabled: viaGroup,
      tooltipText: viaGroup ? "Enrolled via group" : undefined,
    },

    {
      label: "View Client Profile",
      onClick: handleViewClient,
    },
  ];

  return (
    <Card {...other}>
      <Paper className={s.actionArea}>
        <CardHeader
          className={s.header}
          avatar={
            <CardAvatar
              className={s.avatar}
              alt={client.displayName}
              src={client.photoURL}
              children={client.displayName.substring(0, 1).toUpperCase()}
            />
          }
          title={client.displayName}
          titleTypographyProps={{ variant: "h5", className: s.cardTitle }}
          subheader={client.email}
          subheaderTypographyProps={{ className: s.cardSubheader }}
        />
        <CardActions className={s.actions}>
          <Box className={s.metrics}>
            <Typography variant="body2" className={s.text}>
              {startDate} to {endDate}
            </Typography>

            <EnrollmentScore score={unlockedScore} />
          </Box>
          <Button
            {...triggerProps}
            className={s.menuButton}
            children={<MoreHoriz />}
          />
        </CardActions>
      </Paper>

      <ListMenu
        {...menuProps}
        options={menuOptions}
        disabled={unenrollClientInFlight}
      />
    </Card>
  );
}
